/**
 * Development Reset Detection Service
 * 
 * Automatically detects when a database reset has occurred during development
 * and triggers meta-version increment to invalidate client caches.
 * 
 * This provides automatic cache invalidation without manual intervention.
 */

import { supabase } from '@/lib/supabase';

// Key to track the last known database boot time
const DB_BOOT_TIME_KEY = 'terrarium_db_boot_time';

// Development environment check
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Attempts to get a unique identifier for the current database instance
 * In Supabase, we can use the startup_time from pg_postmaster_start_time()
 */
async function getDatabaseBootTime(): Promise<string> {
  try {
    const { data, error } = await supabase
      .rpc('get_database_boot_time', {}, { count: 'exact' })
      .single();
    
    if (error) {
      // Fallback: use current timestamp if function doesn't exist yet
      console.warn('DevelopmentReset: get_database_boot_time function not found, using fallback');
      return Date.now().toString();
    }
    
    return data as string;
  } catch (err) {
    console.warn('DevelopmentReset: Failed to get database boot time:', err);
    return Date.now().toString();
  }
}

/**
 * Gets the stored database boot time from localStorage
 */
function getStoredBootTime(): string | null {
  try {
    return localStorage.getItem(DB_BOOT_TIME_KEY);
  } catch (err) {
    console.warn('DevelopmentReset: Failed to read stored boot time:', err);
    return null;
  }
}

/**
 * Stores the database boot time in localStorage
 */
function setStoredBootTime(bootTime: string): void {
  try {
    localStorage.setItem(DB_BOOT_TIME_KEY, bootTime);
  } catch (err) {
    console.error('DevelopmentReset: Failed to store boot time:', err);
  }
}

/**
 * Detects if a database reset has occurred by comparing boot times
 * Returns true if a reset is detected, false otherwise
 */
export async function detectDatabaseReset(): Promise<boolean> {
  if (!isDevelopment) {
    return false; // Only run in development
  }

  try {
    const currentBootTime = await getDatabaseBootTime();
    const storedBootTime = getStoredBootTime();
    
    // First run - no stored boot time
    if (!storedBootTime) {
      setStoredBootTime(currentBootTime);
      return false;
    }
    
    // Compare boot times
    const hasReset = storedBootTime !== currentBootTime;
    
    if (hasReset) {
      console.log('DevelopmentReset: Database reset detected!', {
        stored: storedBootTime,
        current: currentBootTime
      });
      
      // Update stored boot time
      setStoredBootTime(currentBootTime);
      
      // Trigger meta-version increment
      try {
        await supabase.rpc('increment_meta_version');
        console.log('DevelopmentReset: Meta-version incremented due to database reset');
      } catch (err) {
        console.error('DevelopmentReset: Failed to increment meta-version:', err);
      }
    }
    
    return hasReset;
  } catch (err) {
    console.error('DevelopmentReset: Failed to detect database reset:', err);
    return false;
  }
}

/**
 * Clears stored boot time - useful for testing
 */
export function clearStoredBootTime(): void {
  try {
    localStorage.removeItem(DB_BOOT_TIME_KEY);
  } catch (err) {
    console.error('DevelopmentReset: Failed to clear stored boot time:', err);
  }
}